import React, {useState, useEffect} from 'react';
import {InfiniteScroll, Box, Text, Button, CheckBox} from 'grommet';
import BaseFilter from './BaseFilter';
import {zipIntoObj} from "../../utils/object";
import {
    BILLING_LABELS,
    BILLING_VALUES,
    OVERDUE_RANGE_LABELS,
    OVERDUE_RANGE_VALUES,
    RANGE_LABELS,
    RANGE_VALUES
} from "../../utils/aging-balance-range";

const invoiceAgingRanges = zipIntoObj(RANGE_LABELS, RANGE_VALUES);
const overdueAgingRanges = zipIntoObj(OVERDUE_RANGE_LABELS, OVERDUE_RANGE_VALUES);
const billingTypes = zipIntoObj(BILLING_LABELS, BILLING_VALUES);


const InvoiceFilter = ({ filterName = "Invoice", filterValues=[], onApplyFilter, tab }) => {
    // Values to exclude based on tab
    const getExcludedValues = () => {
        if (tab === 'unpaid') {
            return ['paid', 'has_credit', 'voided'];
        }
        if (tab === 'closed') {
            return ['not_yet_due', 'overdue', ...OVERDUE_RANGE_VALUES, 'quickpay', 'manual_billing', 'billing'];
        }
        return [];
    };
    const excludedValues = getExcludedValues();

    const getSelectedOptions = (values) => {
        const safeValues = Array.isArray(values) ? values : [];
        // Include both aging ranges and billing types
        const allOptions = [...invoiceAgingRanges, ...billingTypes];
        return allOptions.filter(({value}) =>
            [...safeValues, ...(safeValues.includes('overdue') ? OVERDUE_RANGE_VALUES : [])].includes(value)
        );
    };
    const [selectedOptions, setSelectedOptions] = useState(() => getSelectedOptions(filterValues));

    // Sync with filterValues prop when it changes (e.g., from URL)
    useEffect(() => {
        setSelectedOptions(getSelectedOptions(filterValues));
    }, [filterValues]);

    const invoiceFilterOptions = [
        { label: 'Status', value: 'status', isSection: true },
        ...invoiceAgingRanges,
        { label: 'Billing', value: 'billing', isSection: true },
        ...billingTypes
    ].filter(option => !excludedValues.includes(option.value));

    const handleOptionChange = (option) => {
        setSelectedOptions((prevOptions) => {
            let updatedOptions;
            if (option.value === 'overdue') {
                const overdueOptions = Object.values(overdueAgingRanges);
                const allOverdueSelected = overdueOptions.every(opt =>
                    prevOptions.some(selected => selected.value === opt.value)
                );

                if (allOverdueSelected) {
                    updatedOptions = prevOptions.filter(opt =>
                        !overdueOptions.some(overdue => overdue.value === opt.value) && opt.value !== 'overdue'
                    );
                } else {
                    updatedOptions = [...prevOptions, option, ...overdueOptions];
                }
            } else {
                if (prevOptions.some(opt => opt.value === option.value)) {
                    updatedOptions = prevOptions.filter(opt => opt.value !== option.value);
                } else {
                    updatedOptions = [...prevOptions, option];
                }
            }

            // Check if any overdue options are selected
            const overdueOptions = Object.values(overdueAgingRanges);
            const allOverdueSelected = overdueOptions.every(opt =>
                updatedOptions.some(selected => selected.value === opt.value)
            );

            // Remove 'Overdue' option if no overdue options are selected
            if (allOverdueSelected.length !== overdueOptions.length) {
                updatedOptions = updatedOptions.filter(opt => opt.value !== 'overdue');
            }
            // Add 'Overdue' option if all overdue options are selected
            if (allOverdueSelected && !updatedOptions.some(opt => opt.value === 'overdue')) {
                updatedOptions.push(invoiceAgingRanges.find(opt => opt.value === 'overdue'));
            }

            // Remove duplicates
            return updatedOptions.filter((opt, index, self) =>
                index === self.findIndex((t) => t.value === opt.value)
            );
        });
    };


    const isOverdueIndeterminate = (option) => {
        if (option.value !== 'overdue') { return false }
        const overdueOptions = overdueAgingRanges.map(option => option.value);
        const selectedOverdue = selectedOptions.filter(o => overdueOptions.includes(o.value));
        return (selectedOverdue.length > 0 && selectedOverdue.length < overdueOptions.length);
    };

    const renderContent = () => (
        <Box gap="xsmall" responsive={false}>
            <Box height={{min: 'small'}} overflow="auto">
                <InfiniteScroll items={invoiceFilterOptions}>
                    {(option) => (
                        option.isSection ? (
                            <Box key={option.value} pad={{ vertical: 'xsmall' }}>
                                <Text weight="bold" size="small">{option.label}</Text>
                            </Box>
                        ) : (
                            <Box key={option.value} margin={{bottom: 'xsmall'}}>
                                <CheckBox
                                    label={option.label}
                                    checked={selectedOptions.some(item => item.value === option.value) && !isOverdueIndeterminate(option)} //can't be checked and indeterminate
                                    indeterminate={isOverdueIndeterminate(option)}
                                    onChange={() => handleOptionChange(option)}
                                />
                            </Box>
                        )
                    )}
                </InfiniteScroll>
            </Box>
            <Button fill type="submit" primary label="Apply"/>
        </Box>
    );

    const renderButtonLabel = () => {
        if (selectedOptions.length === 0) return null;

        const overdueOptions = OVERDUE_RANGE_VALUES;
        const selectedOverdueCount = selectedOptions.filter(option =>
            overdueOptions.some(overdue => overdue.value === option.value)
        ).length;

        const nonOverdueOptions = selectedOptions.filter(option =>
            !overdueOptions.some(overdue => overdue.value === option.value) && option.label !== 'Overdue'
        );

        let label = '';
        if (selectedOverdueCount > 0) {
            if (selectedOverdueCount === overdueOptions.length) {
                label = 'All overdue';
            } else {
                label = 'Some overdue';
            }
            if (nonOverdueOptions.length > 0) {
                label += ` + ${nonOverdueOptions.length} other${nonOverdueOptions.length > 1 ? 's' : ''}`;
            }
        } else {
            if (nonOverdueOptions.length > 0) {
                label = `${nonOverdueOptions[0].label}`;
                if (nonOverdueOptions.length > 1) {
                    label += ` + ${nonOverdueOptions.length - 1} other${nonOverdueOptions.length > 1 ? 's' : ''}`;
                }
            }
        }

        return label;
    };

    const onApply = () => {
        if (selectedOptions.length === 0) {
            onReset();
        } else {
            const isOverdueSelected = selectedOptions.some(option => option.value === 'overdue');
            const agingOptions = OVERDUE_RANGE_VALUES;

            let results = selectedOptions.reduce((acc, option) => {
                if (isOverdueSelected) {
                    if (!agingOptions.includes(option.value)) {
                        acc.push(option.value);
                    }
                } else {
                    acc.push(option.value);
                }
                return acc;
            }, []);
            onApplyFilter && onApplyFilter(results);
        }
    };

    const onReset = () => {
        setSelectedOptions([]);
        onApplyFilter && onApplyFilter([]);
    };

    return (
        <BaseFilter
            filterName={filterName}
            renderContent={renderContent}
            renderButtonLabel={renderButtonLabel}
            isActive={selectedOptions.length > 0}
            onApply={onApply}
            onReset={onReset}
        />
    );
};

export default InvoiceFilter;